import React, { useState } from "react";
import "./styles.css";

// Подпись колонки текстом — из детей <ListHeaderItem>. Нужна для мобильной вёрстки
// (см. styles.css): на узком экране таблица разбирается в карточки, и каждая ячейка
// подписывает себя сама через data-label. Колонки-кнопки (шапка без текста, напр.
// «добавить»/«просмотр») дают пустую строку — такие ячейки остаются без подписи.
const extractLabel = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractLabel).join('');
    if (React.isValidElement(node)) return extractLabel(node.props.children);
    return '';
};

// Раскладка колонки в КАРТОЧКЕ (только узкий экран, в таблице флаги ничего не меняют).
// Ставятся на <ListHeaderItem> и позволяют странице собрать читаемую карточку вместо
// механической решётки «подпись — значение» по всем колонкам:
//   isCardHead  — в первую строку карточки, инлайн и без подписи (id, имя, статус);
//   isCardWide  — своей строкой во всю ширину (длинные значения: аккаунт, последний скрипт);
//   isSecondary — под «Подробнее», скрыто по умолчанию (служебное: порт, версия провижна);
//   isCardFoot  — последней строкой карточки (основное действие, напр. «Стрим»).
// Без флагов колонка занимает половину ширины — по две в ряд.
//
// Отдельно isAction: колонка не с данными, а с действием (чекбокс «выбрать все», «добавить»).
// Её содержимое из шапки дублируется полоской над карточками, а ячейки строк не подписываются.
// Колонка с пустой шапкой считается такой же — флаг нужен, только когда у действия есть текст.
const CARD_FLAGS = [
    ['isCardHead', 'table__body-item_head'],
    ['isCardWide', 'table__body-item_wide'],
    ['isSecondary', 'table__body-item_secondary'],
    ['isCardFoot', 'table__body-item_foot'],
];

const cardClassName = (item) => CARD_FLAGS
    .filter(([prop]) => item.props[prop])
    .map(([, className]) => className)
    .join(' ');

// Таблица списка: header — массив <ListHeaderItem>, rows — записи,
// renderRow(row) — массив <ListBodyItem> строки.
// className — модификатор таблицы от страницы (напр. table_dense у устройств).
// selectedIds — id строк, отмеченных чекбоксом: такие строки получают table__row_selected.
export default function Table({ header, rows, renderRow, className = '', selectedIds = [] }) {
    const selectedIdSet = new Set(selectedIds);
    const headerItems = React.Children.toArray(header);
    const labels = headerItems.map((item) => extractLabel(item.props.children).trim());
    const cardClasses = headerItems.map(cardClassName);
    // Класс колонки у ячеек тела — по name шапки, как у th (см. ListHeaderItem).
    const columnClasses = headerItems.map((item) => (item.props.name ? `table__body-item_col_${item.props.name}` : ''));
    const actionFlags = headerItems.map((item, index) => Boolean(item.props.isAction) || !labels[index]);
    const hasSecondary = headerItems.some((item) => item.props.isSecondary);

    // Раскрытые карточки — по ключу строки. Состояние здесь, а не в строке: строка рисуется
    // функцией страницы (renderRow), своего компонента у неё нет.
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

    // Колонки-действия живут в ШАПКЕ (напр. «Добавить» на списках нод/пользователей,
    // «Выбрать все» на устройствах). На телефоне шапка скрыта карточной вёрсткой, поэтому
    // такие кнопки дублируем полоской над списком — иначе с телефона ноду просто нечем
    // было бы добавить.
    const headerActions = headerItems.filter((item, index) => actionFlags[index] && item.props.children);

    return <div className="table__wrapper">
        {headerActions.length > 0
            ? <div className="table__mobile-actions">
                {headerActions.map((item) => <React.Fragment key={item.key}>{item.props.children}</React.Fragment>)}
            </div>
            : null}
        <table className={`table ${className}`.trim()}>
            <thead className="table__header">
                <tr>{header}</tr>
            </thead>
            <tbody className="table__body">
                {(rows ?? []).map((row, index) => {
                    const rowKey = row.id ?? index;
                    const isExpanded = Boolean(expandedRows[rowKey]);
                    const isSelected = selectedIdSet.has(row.id);

                    return <tr
                        key={rowKey}
                        className={`table__row${isExpanded ? ' table__row_expanded' : ''}${isSelected ? ' table__row_selected' : ''}`}
                    >
                        {React.Children.toArray(renderRow(row)).map((cell, cellIndex) => {
                            const props = {};
                            if (labels[cellIndex] && !actionFlags[cellIndex]) props['data-label'] = labels[cellIndex];
                            const extraClasses = `${columnClasses[cellIndex] ?? ''} ${cardClasses[cellIndex] ?? ''}`.trim();
                            if (extraClasses) {
                                props.className = `${cell.props.className ?? ''} ${extraClasses}`.trim();
                            }
                            return Object.keys(props).length > 0 ? React.cloneElement(cell, props) : cell;
                        })}
                        {/* Переключатель второстепенных полей. В таблице скрыт (display: none),
                            поэтому на широком экране лишней колонки не появляется. */}
                        {hasSecondary
                            ? <td className="table__more">
                                <button type="button" className="table__more-btn" onClick={() => toggleRow(rowKey)}>
                                    {isExpanded ? '▾ Скрыть' : '▸ Подробнее'}
                                </button>
                            </td>
                            : null}
                    </tr>;
                })}
                {(rows ?? []).length === 0
                    ? <tr className="table__row">
                        <td className="table__empty" colSpan={headerItems.length}>Нет данных</td>
                    </tr>
                    : null}
            </tbody>
        </table>
    </div>;
}
